import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, addDays, subDays } from 'date-fns';
import {
  Scissors, Plus, ChevronLeft, ChevronRight, Clock, User,
  CheckCircle2, AlertCircle, XCircle, Loader2, Calendar,
  Activity, Building2, Filter, Eye, Edit2, Stethoscope,
} from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const STATUS_CONFIG = {
  scheduled:   { label: 'Scheduled',   color: 'bg-blue-50 text-blue-700 border-blue-200',   dot: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  completed:   { label: 'Completed',   color: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  cancelled:   { label: 'Cancelled',   color: 'bg-red-50 text-red-700 border-red-200',       dot: 'bg-red-400' },
  postponed:   { label: 'Postponed',   color: 'bg-gray-50 text-gray-600 border-gray-200',    dot: 'bg-gray-400' },
} as const;

const SURGERY_TYPE_COLOR = {
  elective:  'bg-blue-50 text-blue-700',
  emergency: 'bg-red-50 text-red-700',
  urgent:    'bg-orange-50 text-orange-700',
};

function StatCard({ label, value, icon: Icon, color }: any) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-4 flex items-center gap-4">
      <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', color)}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-navy">{value ?? 0}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function OtCaseCard({ c, onView, onUpdate }: any) {
  const status = STATUS_CONFIG[c.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.scheduled;
  const surgType = SURGERY_TYPE_COLOR[c.surgery_type as keyof typeof SURGERY_TYPE_COLOR] ?? 'bg-gray-50 text-gray-600';

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-gray-400">{c.case_number}</span>
            <span className={clsx('text-2xs px-2 py-0.5 rounded-full border font-medium', status.color)}>
              {status.label}
            </span>
            <span className={clsx('text-2xs px-2 py-0.5 rounded-full font-medium capitalize', surgType)}>
              {c.surgery_type}
            </span>
          </div>
          <p className="text-sm font-semibold text-navy mt-1 truncate">{c.procedure_name}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onView(c)}
            className="p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition"
            title="View details">
            <Eye size={14} />
          </button>
          {c.status === 'scheduled' && (
            <button
              onClick={() => onUpdate(c.id, 'in_progress')}
              className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition"
              title="Start case">
              <Activity size={14} />
            </button>
          )}
          {c.status === 'in_progress' && (
            <button
              onClick={() => onUpdate(c.id, 'completed')}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
              title="Mark completed">
              <CheckCircle2 size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-y-1.5 text-xs">
        <div className="flex items-center gap-1.5 text-gray-500">
          <User size={11} className="flex-shrink-0" />
          <span className="truncate">{c.patient_name}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-500">
          <Clock size={11} className="flex-shrink-0" />
          <span>{c.scheduled_start} {c.scheduled_end ? `– ${c.scheduled_end}` : ''}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-500">
          <Stethoscope size={11} className="flex-shrink-0" />
          <span className="truncate">Dr. {c.surgeon_name}</span>
        </div>
        {c.theatre_name && (
          <div className="flex items-center gap-1.5 text-gray-500">
            <Building2 size={11} className="flex-shrink-0" />
            <span className="truncate">{c.theatre_name}</span>
          </div>
        )}
        {c.anaesthetist_name && (
          <div className="flex items-center gap-1.5 text-gray-400 col-span-2">
            <span>Anaes: Dr. {c.anaesthetist_name} ({c.anaesthesia_type})</span>
          </div>
        )}
      </div>
    </div>
  );
}

function NewCaseModal({ onClose, theatres, surgeons, anaesthetists }: any) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    patient_id: '', admission_id: '', theatre_id: '', scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    scheduled_start: '08:00', scheduled_end: '', surgery_type: 'elective', case_type: 'major',
    procedure_name: '', surgeon_id: '', anaesthetist_id: '', anaesthesia_type: 'general',
    pre_op_diagnosis: '', icd_code: '',
  });
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);

  const { data: patientsData } = useQuery({
    queryKey: ['patient-search-ot', patientSearch],
    queryFn: () => api.get('/patients', { params: { search: patientSearch, limit: 8 } }).then(r => r.data),
    enabled: patientSearch.length > 1,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/ot/cases', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ot-schedule'] });
      qc.invalidateQueries({ queryKey: ['ot-stats'] });
      onClose();
    },
  });

  function setField(k: string, v: string) { setForm(f => ({ ...f, [k]: v })); }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2">
            <Scissors size={18} className="text-orange" /> Schedule OT Case
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Patient search */}
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Patient *</label>
            {selectedPatient ? (
              <div className="flex items-center justify-between bg-orange/5 border border-orange/20 rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-navy">{selectedPatient.name}</p>
                  <p className="text-xs text-gray-500">{selectedPatient.patient_uid} · {selectedPatient.age_years}y {selectedPatient.gender}</p>
                </div>
                <button onClick={() => { setSelectedPatient(null); setForm(f => ({ ...f, patient_id: '' })); }}
                  className="text-xs text-gray-400 hover:text-red-500">Change</button>
              </div>
            ) : (
              <div className="relative">
                <input
                  value={patientSearch}
                  onChange={e => setPatientSearch(e.target.value)}
                  placeholder="Search patient by name or ID..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 focus:border-orange outline-none"
                />
                {patientsData?.data?.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 max-h-48 overflow-y-auto">
                    {patientsData.data.map((p: any) => (
                      <button key={p.id} onClick={() => {
                        setSelectedPatient(p);
                        setForm(f => ({ ...f, patient_id: p.id }));
                        setPatientSearch('');
                      }} className="w-full text-left px-3 py-2 hover:bg-orange/5 text-sm border-b border-gray-50 last:border-0">
                        <span className="font-medium text-navy">{p.name}</span>
                        <span className="text-gray-400 ml-2 text-xs">{p.patient_uid}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Scheduled Date *</label>
              <input type="date" value={form.scheduled_date}
                onChange={e => setField('scheduled_date', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Start Time *</label>
              <input type="time" value={form.scheduled_start}
                onChange={e => setField('scheduled_start', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Theatre</label>
              <select value={form.theatre_id} onChange={e => setField('theatre_id', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                <option value="">Select theatre...</option>
                {theatres?.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Surgery Type *</label>
              <select value={form.surgery_type} onChange={e => setField('surgery_type', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                <option value="elective">Elective</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Procedure Name *</label>
            <input type="text" value={form.procedure_name}
              onChange={e => setField('procedure_name', e.target.value)}
              placeholder="e.g. Appendicectomy (Open), LSCS, Total Knee Replacement..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Pre-operative Diagnosis</label>
            <input type="text" value={form.pre_op_diagnosis}
              onChange={e => setField('pre_op_diagnosis', e.target.value)}
              placeholder="Clinical diagnosis..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Surgeon *</label>
              <select value={form.surgeon_id} onChange={e => setField('surgeon_id', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                <option value="">Select surgeon...</option>
                {surgeons?.map((d: any) => (
                  <option key={d.id} value={d.id}>Dr. {d.name}{d.specialization ? ` (${d.specialization})` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Anaesthetist</label>
              <select value={form.anaesthetist_id} onChange={e => setField('anaesthetist_id', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                <option value="">Select anaesthetist...</option>
                {anaesthetists?.map((d: any) => <option key={d.id} value={d.id}>Dr. {d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Anaesthesia Type</label>
              <select value={form.anaesthesia_type} onChange={e => setField('anaesthesia_type', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                {['general','spinal','epidural','regional','local','sedation','mac'].map(a => (
                  <option key={a} value={a} className="capitalize">{a.charAt(0).toUpperCase() + a.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Case Type</label>
              <select value={form.case_type} onChange={e => setField('case_type', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                <option value="major">Major</option>
                <option value="minor">Minor</option>
                <option value="day_care">Day Care</option>
              </select>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 flex items-center justify-end gap-3">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition">
            Cancel
          </button>
          <button
            onClick={() => {
              if (!form.patient_id || !form.surgeon_id || !form.procedure_name) return;
              createMutation.mutate({
                ...form,
                theatre_id: form.theatre_id || undefined,
                admission_id: form.admission_id || undefined,
                anaesthetist_id: form.anaesthetist_id || undefined,
                scheduled_end: form.scheduled_end || undefined,
              });
            }}
            disabled={createMutation.isPending || !form.patient_id || !form.surgeon_id || !form.procedure_name}
            className="px-5 py-2 rounded-lg bg-orange text-white text-sm font-semibold hover:bg-orange/90
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition">
            {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
            Schedule Case
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OtPage() {
  const qc = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showNewCase, setShowNewCase] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);

  const { data: schedule, isLoading } = useQuery({
    queryKey: ['ot-schedule', selectedDate],
    queryFn: () => api.get('/ot/schedule', { params: { date: selectedDate } }).then(r => r.data),
    refetchInterval: 60_000,
  });

  const { data: stats } = useQuery({
    queryKey: ['ot-stats'],
    queryFn: () => api.get('/ot/stats').then(r => r.data),
    staleTime: 30_000,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users').then(r => r.data),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: any) => api.patch(`/ot/cases/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ot-schedule'] }); qc.invalidateQueries({ queryKey: ['ot-stats'] }); },
  });

  const theatres = schedule?.theatres ?? [];
  const cases = schedule?.cases ?? [];

  const doctors = usersData?.users?.filter((u: any) => u.role === 'doctor') ?? [];
  const allDoctors = usersData?.users?.filter((u: any) => ['doctor','hospital_admin'].includes(u.role)) ?? [];

  function navigateDate(delta: number) {
    const d = new Date(selectedDate);
    setSelectedDate(format(delta > 0 ? addDays(d, 1) : subDays(d, 1), 'yyyy-MM-dd'));
  }

  const casesByTheatre = theatres.reduce((acc: any, t: any) => {
    acc[t.id] = cases.filter((c: any) => c.theatre_id === t.id);
    return acc;
  }, {} as Record<string, any[]>);
  const unassigned = cases.filter((c: any) => !c.theatre_id);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Scissors size={22} className="text-orange" /> Operation Theatre
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">OT scheduling, case management & surgical records</p>
        </div>
        <button
          onClick={() => setShowNewCase(true)}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition shadow-sm">
          <Plus size={15} /> Schedule Case
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Today Scheduled"  value={stats?.today_scheduled}  icon={Calendar}      color="bg-blue-500" />
        <StatCard label="In Progress"       value={stats?.in_progress}       icon={Activity}      color="bg-yellow-500" />
        <StatCard label="Completed Today"   value={stats?.today_completed}   icon={CheckCircle2}  color="bg-green-500" />
        <StatCard label="Cancelled Today"   value={stats?.today_cancelled}   icon={XCircle}       color="bg-red-400" />
        <StatCard label="Month Completed"   value={stats?.month_completed}   icon={Scissors}      color="bg-purple-500" />
      </div>

      {/* Date navigator */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-card px-4 py-3 mb-5">
        <button onClick={() => navigateDate(-1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500">
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-3">
          <Calendar size={15} className="text-orange" />
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="text-sm font-semibold text-navy border-0 outline-none cursor-pointer"
          />
          <span className="text-xs text-gray-400">
            {format(new Date(selectedDate + 'T00:00:00'), 'EEEE, d MMMM yyyy')}
          </span>
        </div>
        <button onClick={() => navigateDate(1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* OT Schedule */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-orange" />
        </div>
      ) : cases.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card flex flex-col items-center justify-center py-16 text-gray-400">
          <Scissors size={40} className="mb-3 opacity-30" />
          <p className="text-base font-medium text-navy">No OT cases scheduled</p>
          <p className="text-sm mt-1">for {format(new Date(selectedDate + 'T00:00:00'), 'd MMMM yyyy')}</p>
          <button onClick={() => setShowNewCase(true)}
            className="mt-4 flex items-center gap-2 bg-orange/10 text-orange border border-orange/20
                       px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange/15 transition">
            <Plus size={14} /> Schedule First Case
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {theatres.map((t: any) => {
            const theatreCases = casesByTheatre[t.id] ?? [];
            if (!theatreCases.length) return null;
            return (
              <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 bg-navy/3">
                  <Building2 size={15} className="text-orange" />
                  <h3 className="font-semibold text-navy text-sm">{t.name}</h3>
                  <span className="text-xs text-gray-400 capitalize">({t.theatre_type?.replace('_',' ')})</span>
                  <span className="ml-auto text-xs font-medium text-gray-500">{theatreCases.length} case{theatreCases.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {theatreCases.map((c: any) => (
                    <OtCaseCard
                      key={c.id}
                      c={c}
                      onView={setSelectedCase}
                      onUpdate={(id: string, status: string) => updateStatus.mutate({ id, status })}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {unassigned.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-50">
                <AlertCircle size={15} className="text-yellow-500" />
                <h3 className="font-semibold text-navy text-sm">Theatre Not Assigned</h3>
                <span className="ml-auto text-xs text-gray-400">{unassigned.length} case{unassigned.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {unassigned.map((c: any) => (
                  <OtCaseCard
                    key={c.id}
                    c={c}
                    onView={setSelectedCase}
                    onUpdate={(id: string, status: string) => updateStatus.mutate({ id, status })}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Case Detail Modal */}
      {selectedCase && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCase(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-navy">{selectedCase.procedure_name}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{selectedCase.case_number}</p>
              </div>
              <button onClick={() => setSelectedCase(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-gray-400">Patient</p><p className="font-medium text-navy">{selectedCase.patient_name}</p><p className="text-xs text-gray-400">{selectedCase.patient_uid}</p></div>
                <div><p className="text-xs text-gray-400">Surgeon</p><p className="font-medium">Dr. {selectedCase.surgeon_name}</p></div>
                <div><p className="text-xs text-gray-400">Scheduled</p><p className="font-medium">{selectedCase.scheduled_date} at {selectedCase.scheduled_start}</p></div>
                <div><p className="text-xs text-gray-400">Theatre</p><p className="font-medium">{selectedCase.theatre_name ?? '—'}</p></div>
                <div><p className="text-xs text-gray-400">Anaesthesia</p><p className="font-medium capitalize">{selectedCase.anaesthesia_type} {selectedCase.anaesthetist_name ? `– Dr. ${selectedCase.anaesthetist_name}` : ''}</p></div>
                <div><p className="text-xs text-gray-400">Surgery Type</p><p className="font-medium capitalize">{selectedCase.surgery_type} / {selectedCase.case_type}</p></div>
              </div>
              {selectedCase.pre_op_diagnosis && (
                <div><p className="text-xs text-gray-400">Pre-op Diagnosis</p><p className="text-sm">{selectedCase.pre_op_diagnosis}</p></div>
              )}
              {selectedCase.operative_notes && (
                <div><p className="text-xs text-gray-400">Operative Notes</p><p className="text-sm whitespace-pre-line">{selectedCase.operative_notes}</p></div>
              )}
            </div>
            <div className="px-5 pb-5 flex gap-3">
              {selectedCase.status === 'scheduled' && (
                <button onClick={() => { updateStatus.mutate({ id: selectedCase.id, status: 'in_progress' }); setSelectedCase(null); }}
                  className="flex-1 py-2 bg-yellow-500 text-white rounded-xl text-sm font-semibold hover:bg-yellow-600 transition">
                  Start Case
                </button>
              )}
              {selectedCase.status === 'in_progress' && (
                <button onClick={() => { updateStatus.mutate({ id: selectedCase.id, status: 'completed' }); setSelectedCase(null); }}
                  className="flex-1 py-2 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition">
                  Mark Completed
                </button>
              )}
              <button onClick={() => setSelectedCase(null)}
                className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewCase && (
        <NewCaseModal
          onClose={() => setShowNewCase(false)}
          theatres={theatres}
          surgeons={allDoctors}
          anaesthetists={allDoctors}
        />
      )}
    </div>
  );
}
